import { useCallback, useRef, useState } from "react"

import { Button, Checkbox, TabItem } from "@atoms"

import { Dialog, TabStack } from "@molecules"

export default function PDFDownloadDialog(props) {
    const { isOpen, onOpenChange, objects, onDownload, showToggle } = props

    const [selectedObjects, setSelectedObjects] = useState([objects[0]])
    const [isPublicVersion, setIsPublicVersion] = useState(false)
    const [error, setError] = useState(false)

    // Store onOpenChange in a ref so the callback identity stays stable
    // across renders. The Popover's useEffect depends on onOpenChange,
    // and an unstable reference causes an infinite re-render loop.
    const onOpenChangeRef = useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange

    const stableOnOpenChange = useCallback((open) => {
        onOpenChangeRef.current(open)
    }, [])

    const handleToggle = (obj) => {
        setSelectedObjects((prevSelected) =>
            prevSelected.includes(obj) ? prevSelected.filter((item) => item !== obj) : [...prevSelected, obj],
        )
    }

    const handleDownload = () => {
        if (selectedObjects.length === 0) {
            setError(true)
        } else {
            onDownload(selectedObjects, isPublicVersion)
        }
    }

    return (
        <Dialog.Root mode="dialog" variant="default" isOpen={isOpen} onOpenChange={stableOnOpenChange}>
            <Dialog.Content>
                <Dialog.Header.Root>
                    <Dialog.Header.Title title="Customize PDF Download" />
                    <Dialog.Header.CloseButton />
                </Dialog.Header.Root>
                <Dialog.Body>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "start",
                            padding: "var(--size-4)",
                            gap: "var(--size-2)",
                        }}
                    >
                        <p>Select which objects you'd like to download:</p>
                        {objects
                            .filter((obj) => obj !== null)
                            .map((obj, _index, filtered) => (
                                <Checkbox
                                    key={obj}
                                    checked={selectedObjects.includes(obj)}
                                    onCheckedChange={() => handleToggle(obj)}
                                    label={obj}
                                    showLabel={true}
                                    isControlled={false}
                                    isDisabled={filtered.length === 1}
                                />
                            ))}
                        </div>
                        {showToggle && (
                            <div style={{
                                width: "var(--size-full)",
                                padding: "var(--size-2) var(--size-4)",
                            }}>
                                <p>Select which version you'd like to download:</p>
                                <TabStack variant="contained" size="md">
                                    <TabItem
                                        isSelected={!isPublicVersion}
                                        onClick={() => setIsPublicVersion(false)}
                                        noDebounce={true}
                                    >
                                        Internal
                                    </TabItem>
                                    <TabItem
                                        isSelected={isPublicVersion}
                                        onClick={() => setIsPublicVersion(true)}
                                        noDebounce={true}
                                    >
                                        Public
                                    </TabItem>
                                </TabStack>
                            </div>
                        )}
                        {error && (
                            <span style={{
                                width: "var(--size-full)",
                                padding: "var(--size-2) var(--size-4)",
                            }} className="text-invalid">
                                <strong>Please select at least one object to download.</strong>
                            </span>
                        )}
                </Dialog.Body>
                <Dialog.Footer.Root>
                    <Button
                        variant="solid"
                        size="lg"
                        onClick={handleDownload}
                        isFullWidth={true}
                    >
                        Download PDF
                    </Button>
                </Dialog.Footer.Root>
            </Dialog.Content>
        </Dialog.Root>
    )
}
